// Algolia-style re-ranking of MiniSearch results.
//
// Purely local: it reproduces the ordering Algolia's settings produced, with no
// Algolia client or network involved, and every tuned profile — production
// included — runs through it. It outlived Algolia on purpose; the name records
// what it imitates.
//
// ─── Why this exists ───────────────────────────────────────────────────────
//
// Six days of paired shadow data (tuned-v3 vs tuned-v4) established that no
// BM25 parameter closes the gap to Algolia: moving `bm25.b` from 0.7 to 0.3
// left 98.4% of result sets byte-identical, and rank correlation against
// Algolia sat at 0.156 throughout. The reason is structural:
//
//   Algolia ranks by a LEXICOGRAPHIC tie-break chain.
//   BM25 ranks by a WEIGHTED SUM.
//
// In Algolia a document with one fewer typo wins outright — no amount of term
// frequency in another field can overturn it. In BM25 a high enough body score
// can always outweigh a title match. No assignment of field boosts makes a
// weighted sum behave lexicographically.
//
// This module keeps MiniSearch as the MATCHER (which documents are candidates)
// and replaces it as the RANKER, comparing candidates on Algolia's documented
// criteria in Algolia's documented order.
//
// ─── What the production index settings actually say ───────────────────────
//
// Fetched 2026-09-24 from the live indices. This corrected an assumption that
// the first draft of this file was built on:
//
//   index           searchableAttributes   customRanking (prod)
//   repos           not set                desc(clientModified)
//   folders         not set                none
//   sets_questions  not set                none
//
// **No index sets `searchableAttributes`.** Every attribute is searchable and
// they are all equal, so Algolia's `attribute` criterion has no field priority
// to apply — a match in `name` does not outrank a match in `questionBodies` by
// virtue of the field it landed in.
//
// That looks like it contradicts the telemetry, which is emphatic: over 14 days
// documents whose title contained the query sat at mean Algolia position 1.25
// against 2.61 for body-only matches, 88.5% vs 61.5% in the top three. Both are
// true, because the `attribute` criterion has two components and only the first
// is inert:
//
//   1. the attribute's index in searchableAttributes   ← nothing set, no effect
//   2. the POSITION OF THE MATCHED WORD within it      ← still decides
//
// A query word at position 0 of a three-word set name beats the same word at
// position 47 of a question body, and for multi-word queries proximity compounds
// it. The title preference is real; it is produced positionally, not by field
// weighting. This file models the mechanism rather than the symptom.
//
// CORRECTED 2026-09-30, see "Record position" below: on `sets_questions` the
// position is counted across the whole record, question text first, so the
// per-attribute model here agrees with Algolia's order for only 37% of the
// pairs this criterion decides. tuned-v11 switches to the record model.
//
// The practical consequence for the wider tuning effort: BASELINE_BOOSTS /
// TUNED_BOOSTS in buildMiniSearchIndices are a deliberate DEPARTURE from
// Algolia, not an imitation of it. They approximate a positional effect with
// field weights, which is why narrowing the boost gap 6:1 → 2:1 in tuned-v2
// moved body-only losses by only 1.5 points. They remain a useful
// precision/recall knob; they are not a ranking knob, and no ratio converges on
// Algolia's ordering.
//
// ─── Why re-ranking moves F1 and not just ordering ─────────────────────────
//
// F1, precision and recall are set metrics: reordering cannot change them. It
// changes them here because the caller slices a fixed number of hits off the
// top, so on a capped surface ORDER DECIDES MEMBERSHIP. The cap is tightest
// where quality is worst and traffic heaviest:
//
//   topNav         cap 4    64% of searches   tuned-v3 F1 0.594
//   searchResults  cap 100  21%               F1 0.739
//   sectionHome    cap 200  15%               F1 0.832
//
// On topNav both engines return ~3.5 of a possible 4, so the disagreement is
// almost entirely about WHICH three or four. On sectionHome nothing is capped
// and this only changes the order a user sees.

import { tokenizeQuery } from './textNormalize'
import { documentFieldTexts } from './documentFields'

// Which attributes each index searches. A SET, not an order — the ordering, where
// production declares one, lives in ATTRIBUTE_PRIORITY below. This exists only
// to know which fields to look in for position and proximity.
export const SEARCHABLE_ATTRIBUTES = {
  repos: ['name', 'description'],
  folders: ['name'],
  setsQuestions: ['name', 'description', 'body', 'questionBodies', 'questionChoices', 'choices'],
}

// Attribute PRIORITY, where production declares one. Resolved 2026-09-24 from
// the legacy `attributesToIndex` key — which is why it read as "not set":
//
//   repos           ["name","description"]  ordered: name outranks description
//   folders         null                    unset: every attribute equal
//   sets_questions  null                    unset: every attribute equal
//
// So the `attribute` criterion's field-index component applies to `repos` only.
// For the other two — about 98% of traffic — it is inert, and what discriminates
// is the position of the match WITHIN the attribute. See §0f.1.
const ATTRIBUTE_PRIORITY = {
  repos: ['name', 'description'],
  folders: null,
  setsQuestions: null,
}

// The `custom` stage — Algolia's `customRanking`, applied last, per index.
//
// Production values as fetched above. Note `repos` differs from the other two,
// and that DEV differs from PROD on folders and sets_questions (dev carries
// desc(clientModified) everywhere). These values track PRODUCTION, because that
// is where the shadow lanes run and what the quality reports are scored against.
const CUSTOM_RANKING = {
  repos: 'clientModified',
  folders: null,
  setsQuestions: null,
}

// Algolia caps the proximity criterion; beyond this distance matches are
// treated as equally far apart.
const MAX_PROXIMITY = 8

// Upper bound on the text scanned per attribute when locating a match.
// `questionBodies` for a large set can run to several kilobytes and this runs
// on every keystroke, so the tail is bounded: past this point the exact word
// index no longer changes the ordering in any way a user could notice.
const MAX_SCAN_CHARS = 4000

const NO_POSITION = 9999

// ─── Record position (tuned-v11) ───────────────────────────────────────────
//
// Measured 2026-09-30 against the captured production corpus (search-dev-corpus;
// SEARCH_QUALITY_ANALYTICS.md §0j), and it corrects the header above.
//
// On `sets_questions`, which configures no searchable attributes, Algolia's
// `firstMatchedWord` is NOT the position inside the matching attribute. It is
// the position of the first match across the WHOLE RECORD, counted in the order
// the record stores its attributes -- and a set record stores its question text
// before its name:
//
//   set        _questionBodies, _questionChoices, …, name, description
//   question   _questionChoices, …, body
//
// So a set whose first question mentions the word outranks a set whose title is
// the word, whenever the earlier criteria tie. On single-word queries, over
// pairs of hits Algolia ordered by this criterion, a running count in this
// order reproduces Algolia's order for 89.5% of pairs; the per-attribute
// position above, 37%. The `attribute` criterion decides ~80% of all pairs, so
// this is most of what separates local order from Algolia's.
//
// Reproducing it matches Algolia; whether it is the better ranking for a user
// is a separate question, which is why it is a profile switch and not a fix.
const RECORD_ORDER = {
  set: ['questionBodies', 'questionChoices', 'name', 'description'],
  question: ['choices', 'body'],
}

// tuned-v12: the same running count with the title first -- a set's name and
// description, a question's own body -- so a title match outranks a match deep
// in question text whenever the earlier criteria tie. A deliberate departure
// from Algolia, to be judged on what users do rather than on agreement with it.
const TITLE_FIRST_RECORD_ORDER = {
  set: ['name', 'description', 'questionBodies', 'questionChoices'],
  question: ['body', 'choices'],
}

// Position of the first query-term match across the record, laid out as
// `layout` (RECORD_ORDER or TITLE_FIRST_RECORD_ORDER). Null when the kind has
// no layout (repos and folders keep the per-attribute model: repos declares an
// attribute order, folders index a name only).
function recordFirstMatchedWord(original, kind, queryTerms, layout) {
  const order = layout[kind]
  if (!order) return null
  let offset = 0
  for (let f = 0; f < order.length; f += 1) {
    const elements = documentFieldTexts(original, kind, order[f])
    for (let e = 0; e < elements.length; e += 1) {
      const words = tokenizeCapped(elements[e])
      const positions = termPositions(words, queryTerms)
      let earliest = NO_POSITION
      for (let t = 0; t < positions.length; t += 1) {
        if (positions[t].length > 0 && positions[t][0] < earliest) earliest = positions[t][0]
      }
      if (earliest !== NO_POSITION) return offset + earliest
      offset += words.length
    }
  }
  return NO_POSITION
}

// Levenshtein distance, bounded at `max`. Returns max + 1 once the true
// distance provably exceeds the bound, so the common "completely different
// word" case exits early instead of filling an O(n*m) table.
function boundedLevenshtein(a, b, max) {
  if (a === b) return 0
  if (Math.abs(a.length - b.length) > max) return max + 1
  let prev = new Array(b.length + 1)
  let curr = new Array(b.length + 1)
  for (let j = 0; j <= b.length; j += 1) prev[j] = j
  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i
    let rowMin = curr[0]
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
      if (curr[j] < rowMin) rowMin = curr[j]
    }
    if (rowMin > max) return max + 1
    const swap = prev
    prev = curr
    curr = swap
  }
  return prev[b.length]
}

// How one query term was satisfied by one matched index term.
//
// Algolia counts a prefix match as ZERO typos — `prefixLast` extends the last
// word, it does not correct it. Only a genuine edit counts, which is why this
// returns 0 for both the exact and the prefix case and separates them via
// `exact`.
function classifyTerm(queryTerm, matchedTerm) {
  if (matchedTerm === queryTerm) return { typos: 0, exact: true }
  if (matchedTerm.startsWith(queryTerm)) return { typos: 0, exact: false }
  return { typos: boundedLevenshtein(queryTerm, matchedTerm, 2), exact: false }
}

// Word positions of every query term inside one attribute's text.
//
// Returns one sorted position list per query term (empty when that term does
// not occur here). Prefix matching mirrors the tuned profile's `prefix`
// predicate, so a term the matcher accepted is a term this can locate.
function termPositions(words, queryTerms) {
  const last = queryTerms.length - 1
  const positions = queryTerms.map(() => [])
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i]
    for (let t = 0; t < queryTerms.length; t += 1) {
      // `queryType: "prefixLast"` — only the final term prefix-matches. Allowing
      // it on every term credited "cat" at word 0 of "category ...", a position
      // Algolia would never award. See §0f.7.
      const hit = t === last ? word.startsWith(queryTerms[t]) : word === queryTerms[t]
      if (hit) positions[t].push(i)
    }
  }
  return positions
}

function tokenizeCapped(text) {
  return tokenizeQuery(text.length > MAX_SCAN_CHARS ? text.slice(0, MAX_SCAN_CHARS) : text)
}

// Smallest window of consecutive word positions containing at least one
// occurrence of every query term — Algolia's proximity, measured within a
// single attribute.
//
// Returns null when this attribute does not contain every term, because
// proximity between words that are not both here is undefined rather than
// infinite; the caller falls back to other attributes.
function minimumSpan(positions) {
  const flat = []
  for (let t = 0; t < positions.length; t += 1) {
    if (positions[t].length === 0) return null
    for (let i = 0; i < positions[t].length; i += 1) flat.push([positions[t][i], t])
  }
  flat.sort((a, b) => a[0] - b[0])

  const seen = new Array(positions.length).fill(0)
  let covered = 0
  let best = Infinity
  let lo = 0
  for (let hi = 0; hi < flat.length; hi += 1) {
    if (seen[flat[hi][1]] === 0) covered += 1
    seen[flat[hi][1]] += 1
    while (covered === positions.length) {
      const span = flat[hi][0] - flat[lo][0]
      if (span < best) best = span
      seen[flat[lo][1]] -= 1
      if (seen[flat[lo][1]] === 0) covered -= 1
      lo += 1
    }
  }
  return best === Infinity ? null : best
}

// The `custom` stage value, lower being better so the comparator stays
// direction-free. Only `repos` has a customRanking in production
// (`desc(clientModified)`, so newer wins); everywhere else this is inert.
//
// A record missing the attribute sorts last, which is how Algolia treats a
// missing customRanking value.
// Rank of the matched attribute, when the index declares an order. Indices with
// no declared order return 0 for everything, leaving the criterion inert.
function attributePriorityRank(indexKey, fields) {
  const order = ATTRIBUTE_PRIORITY[indexKey]
  if (!order) return 0
  let best = order.length
  for (let i = 0; i < fields.length; i += 1) {
    const rank = order.indexOf(fields[i])
    if (rank !== -1 && rank < best) best = rank
  }
  return best
}

function customRankValue(result, indexKey) {
  const attribute = CUSTOM_RANKING[indexKey]
  if (!attribute) return 0
  const raw = (result._original || {})[attribute]
  const time = raw ? Date.parse(raw) : NaN
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : -time
}

/**
 * Compute the Algolia-style ranking signals for one MiniSearch result.
 *
 * MiniSearch hands back `terms` (matched index terms, already folded) and
 * `match` ({ matchedTerm: [fieldNames] }), and `_original` carries the source
 * record — together enough to reconstruct every criterion except `custom`,
 * which is read from the record directly.
 *
 * @param {Object} result             a raw MiniSearch search result
 * @param {Array<string>} queryTerms  folded query terms
 * @param {string} indexKey           'repos' | 'folders' | 'setsQuestions'
 * @param {{ recordPosition?: boolean, titleFirst?: boolean }} [options]
 *   `recordPosition` measures `firstMatchedWord` across the whole record, as
 *   Algolia does (tuned-v11); `titleFirst` lays the record out title first
 *   (tuned-v12)
 * @returns {Object} signals, all "lower is better" except `score`
 */
export function rankSignals(result, queryTerms, indexKey, options = {}) {
  const match = result.match || {}
  const matchedTerms = Object.keys(match)

  let typos = 0
  let wordsMissed = 0
  let exactWords = 0
  const attributesWithMatch = new Set()

  for (let q = 0; q < queryTerms.length; q += 1) {
    const queryTerm = queryTerms[q]
    let termTypos = Infinity
    let termExact = false

    for (let m = 0; m < matchedTerms.length; m += 1) {
      const matchedTerm = matchedTerms[m]
      const { typos: d, exact } = classifyTerm(queryTerm, matchedTerm)
      // Only terms within typo range are evidence that THIS query word was
      // satisfied; an unrelated index term matching some other query word is
      // not, and must not lower this word's typo count.
      if (d <= 2 && d < termTypos) {
        termTypos = d
        termExact = exact
      }
      if (d <= 2) {
        const fields = match[matchedTerm] || []
        for (let f = 0; f < fields.length; f += 1) attributesWithMatch.add(fields[f])
      }
    }

    if (termTypos === Infinity) {
      wordsMissed += 1
    } else {
      typos += termTypos
      if (termExact) exactWords += 1
    }
  }

  // Position, proximity and exactness, over the attributes that actually
  // matched. Each attribute is evaluated PER ELEMENT, because `_questionBodies`
  // and `_questionChoices` are arrays in the Algolia record: word position
  // restarts at 0 in every element and proximity never spans two of them. See
  // §0f.2.
  const original = result._original || {}
  const kind = result._kind || original._kind
  let firstMatchedWord = NO_POSITION
  let proximity = MAX_PROXIMITY
  let attributeRank = ATTRIBUTE_PRIORITY[indexKey] ? ATTRIBUTE_PRIORITY[indexKey].length : 0
  let wholeAttributeMatch = false
  const searchable = SEARCHABLE_ATTRIBUTES[indexKey] || SEARCHABLE_ATTRIBUTES.setsQuestions
  const matchedFields = []

  for (let a = 0; a < searchable.length; a += 1) {
    const field = searchable[a]
    const elements = attributesWithMatch.has(field) ?
      documentFieldTexts(original, kind, field) :
      []
    let fieldMatched = false

    for (let e = 0; e < elements.length; e += 1) {
      const words = tokenizeCapped(elements[e])
      if (words.length > 0) {
        const positions = termPositions(words, queryTerms)
        for (let t = 0; t < positions.length; t += 1) {
          if (positions[t].length > 0) {
            fieldMatched = true
            if (positions[t][0] < firstMatchedWord) firstMatchedWord = positions[t][0]
          }
        }
        if (queryTerms.length > 1) {
          const span = minimumSpan(positions)
          if (span !== null && span < proximity) proximity = span
        }
        // `exactOnSingleWordQuery: "attribute"` — for a one-word query Algolia
        // scores `exact` only when the query matches an ENTIRE attribute, not a
        // word inside it. So "Fractions" is exact and "Introduction to
        // Fractions" is not, which is a strong title preference that needs no
        // field priority at all. See §0f.1b.
        if (queryTerms.length === 1 && words.length === 1 && words[0] === queryTerms[0]) {
          wholeAttributeMatch = true
        }
      }
    }
    if (fieldMatched) matchedFields.push(field)
  }

  if (matchedFields.length > 0) {
    attributeRank = attributePriorityRank(indexKey, matchedFields)
  }
  if (options.recordPosition) {
    const layout = options.titleFirst ? TITLE_FIRST_RECORD_ORDER : RECORD_ORDER
    const recordPosition = recordFirstMatchedWord(original, kind, queryTerms, layout)
    if (recordPosition !== null) firstMatchedWord = recordPosition
  }
  if (queryTerms.length <= 1) proximity = 0

  return {
    typos,
    wordsMissed,
    proximity,
    // Algolia's `attribute` criterion has two components, in this order: the
    // attribute's index in the declared list (inert where none is declared),
    // then the position of the match within it.
    attributeRank,
    firstMatchedWord,
    // Negated so every field here is "lower is better", keeping the comparator
    // free of per-field direction flags.
    // Single-word queries use Algolia's whole-attribute rule; multi-word queries
    // keep the per-term count, which is what `exact` means there.
    exactNegated: queryTerms.length === 1 ? -(wholeAttributeMatch ? 1 : 0) : -exactWords,
    custom: customRankValue(result, indexKey),
    score: result.score,
  }
}

// Algolia's criteria in Algolia's order. `geo` and `filters` are omitted: this
// deployment uses neither (the only filter is `archived`, applied to every
// query equally, so it can never discriminate between two hits).
const CRITERIA = [
  'typos', 'wordsMissed', 'proximity', 'attributeRank', 'firstMatchedWord', 'exactNegated', 'custom',
]

/**
 * Compare two results the way Algolia would: strict lexicographic order over
 * CRITERIA, with BM25 score as a final deterministic tie-break.
 *
 * The strictness is the point. The first criterion that differs decides the
 * pair outright, so a well-positioned match can never be overturned by body
 * text frequency — which is what a weighted sum allows and what has been
 * costing us rank correlation.
 *
 * The score tie-break is a local invention, not an imitation: where
 * customRanking is empty Algolia's residual order is internal and arbitrary, so
 * there is nothing to reproduce. It only ever separates candidates that every
 * Algolia criterion left tied.
 */
export function compareAlgoliaStyle(a, b) {
  for (let i = 0; i < CRITERIA.length; i += 1) {
    const key = CRITERIA[i]
    if (a[key] !== b[key]) return a[key] - b[key]
  }
  return b.score - a.score
}

/**
 * Re-rank a MiniSearch result list into Algolia-like order.
 *
 * Membership is untouched — this is a sort, not a filter. It changes which
 * documents survive only where the caller slices to a cap, which is the
 * mechanism described in the header.
 *
 * @param {Array<Object>} results  raw MiniSearch results, any order
 * @param {string} query           the raw user query
 * @param {string} indexKey        'repos' | 'folders' | 'setsQuestions'
 * @param {{ recordPosition?: boolean, titleFirst?: boolean }} [options] see
 *   rankSignals
 * @returns {Array<Object>} the same result objects, re-ordered
 */
export function applyAlgoliaRerank(results, query, indexKey, options = {}) {
  if (!Array.isArray(results) || results.length < 2) return results
  const queryTerms = tokenizeQuery(query)
  if (queryTerms.length === 0) return results

  // Decorate-sort-undecorate: signals are computed once per result rather than
  // once per comparison, so the attribute text is scanned O(n) times and not
  // O(n log n) times.
  const decorated = results.map((result, i) => ({
    result,
    // Carried through so the sort is stable across engines that do not
    // guarantee it — two candidates identical on every criterion must not
    // reshuffle between keystrokes.
    i,
    signals: rankSignals(result, queryTerms, indexKey, options),
  }))

  decorated.sort((x, y) => {
    const cmp = compareAlgoliaStyle(x.signals, y.signals)
    return cmp !== 0 ? cmp : x.i - y.i
  })

  return decorated.map((d) => d.result)
}
